import { useFilteredAndFlattenedShapes } from './useFilteredAndFlattenedShapes';
import { useShapesList } from '@ais-ui/api';
import { useMemo } from 'react';

export const useInfiniteSelectShapes = (
  search: string,
  allowedGeometryTypes?: string[],
) => {
  const { data, isLoading } = useShapesList();
  const shapes = data?.records ? data.records : [];

  // Get all flattened shapes (unfiltered) for finding selected value
  const flattenedShapes = useFilteredAndFlattenedShapes(
    shapes,
    '',
    allowedGeometryTypes,
  );

  // Get filtered shapes based on search
  const filteredFlattenedShapes = useFilteredAndFlattenedShapes(
    shapes,
    search,
    allowedGeometryTypes,
  );

  // Sort shapes while maintaining parent-child relationships
  const sortedFilteredShapes = useMemo(() => {
    if (!filteredFlattenedShapes.length) return [];

    // Build parent-child map based on position and level
    const childrenMap = new Map<number, number[]>();

    // Find children for each FeatureCollection
    filteredFlattenedShapes.forEach((shape, index) => {
      if (shape.type === 'FeatureCollection') {
        const children: number[] = [];
        const parentLevel = shape.level || 0;

        // Look ahead for children (next items with higher level)
        for (let i = index + 1; i < filteredFlattenedShapes.length; i++) {
          const nextShape = filteredFlattenedShapes[i];
          const nextLevel = nextShape.level || 0;

          // If we hit another item at same or lower level, stop
          if (nextLevel <= parentLevel) break;

          // If it's a direct child (one level deeper), add it
          if (nextLevel === parentLevel + 1) {
            children.push(i);
          }
        }

        if (children.length > 0) {
          childrenMap.set(index, children);
        }
      }
    });

    // Recursive function to process and sort a group
    const processGroup = (indices: number[]): number[] => {
      // Sort indices by shape name
      const sorted = [...indices].sort((a, b) => {
        const nameA = filteredFlattenedShapes[a].name?.toLowerCase() || '';
        const nameB = filteredFlattenedShapes[b].name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });

      // Build result with children after parents
      const result: number[] = [];
      sorted.forEach((index) => {
        result.push(index);

        // If this has children, recursively sort and add them
        const children = childrenMap.get(index);
        if (children) {
          result.push(...processGroup(children));
        }
      });

      return result;
    };

    // Find all root items (level 0 or items without parents)
    const rootIndices = filteredFlattenedShapes
      .map((_, index) => index)
      .filter((index) => {
        const shape = filteredFlattenedShapes[index];
        const level = shape.level || 0;

        // It's a root if it's level 0
        if (level === 0) return true;

        // Or if there's no parent before it at a lower level
        for (let i = index - 1; i >= 0; i--) {
          const prevShape = filteredFlattenedShapes[i];
          if (
            prevShape.type === 'FeatureCollection' &&
            (prevShape.level || 0) < level
          ) {
            return false; // Found a parent
          }
        }
        return true; // No parent found
      });

    // Process all roots and their children
    const sortedIndices = processGroup(rootIndices);

    // Map indices back to shapes
    return sortedIndices.map((index) => filteredFlattenedShapes[index]);
  }, [filteredFlattenedShapes]);

  return {
    filteredFlattenedShapes: sortedFilteredShapes,
    isLoading,
    flattenedShapes,
  };
};
